/**
 * MapCleanupUtility - Utility for cleaning up Map data structures
 * 
 * Extracted from TypesetterWorkerClient for better modularity and testability.
 * 
 * This utility provides functions for managing Map size limits and cleanup,
 * useful for preventing memory bloat in caches and data structures.
 */

/**
 * Clean up a Map to keep only the most recent entries
 * 
 * Removes the oldest entries from a Map when it exceeds the maximum size.
 * Keeps the most recent entries (last N entries).
 * 
 * @param map - Map to clean up
 * @param maxSize - Maximum number of entries to keep (default: 100)
 * @returns Number of entries removed
 */
export function cleanupMap<K, V>(map: Map<K, V>, maxSize: number = 100): number {
  if (map.size <= maxSize) {
    return 0;
  }
  
  const entries = Array.from(map.entries());
  const toDelete = entries.slice(0, entries.length - maxSize);
  const deleteCount = toDelete.length;
  
  for (const [key] of toDelete) {
    map.delete(key);
  }
  
  return deleteCount;
}

/**
 * Get entries to delete from a Map when it exceeds max size
 * 
 * Returns the keys that should be deleted to bring the Map down to maxSize.
 * Does not modify the Map - caller must delete the keys.
 * 
 * @param map - Map to analyze
 * @param maxSize - Maximum number of entries to keep
 * @returns Array of keys to delete
 */
export function getKeysToDelete<K, V>(map: Map<K, V>, maxSize: number = 100): K[] {
  if (map.size <= maxSize) {
    return [];
  }
  
  const entries = Array.from(map.entries());
  const toDelete = entries.slice(0, entries.length - maxSize);
  return toDelete.map(([key]) => key);
}

